import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Share2, Globe, Printer } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import Button from './ui/Button';
import type { Note } from '../types';
import { formatDate } from '../lib/utils';
import BasicCodeBlock from './BasicCodeBlock';
import CustomTable, { CustomThead, CustomTh, CustomTr, CustomTd } from './CustomTable';
import CustomCheckbox from './CustomCheckbox';
import FootnoteBackButton from './FootnoteBackButton';
import AvatarTooltip from './ui/AvatarTooltip';
import PrintModal, { PrintOptions } from './PrintModal';
import { supabase } from '../lib/supabase';

interface NoteViewerProps {
  note: Note;
  onClose: () => void;
  onShare: () => void;
  user?: {
    name: string;
    avatar_url?: string;
  };
}

const NoteViewer: React.FC<NoteViewerProps> = ({ note, onClose, onShare, user }) => {
  const [tags, setTags] = useState<any[]>([]);
  const [showPrintModal, setShowPrintModal] = useState(false);

  useEffect(() => {
    const fetchTags = async () => {
      if (!note.tags) return;

       // Filter out non-UUID tag IDs
       const validTagIds = note.tags.filter((tagId) => {
        // Basic UUID check (more robust validation might be needed)
        return typeof tagId === 'string' && /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(tagId);
      });
      
      try {
        const { data: customTags, error } = await supabase
          .from('tags')
          .select('*')
          .in('id', validTagIds);

        if (error) {
          console.error('Error fetching tags (Supabase error):', error); // Log Supabase error
          throw error; // Re-throw to trigger the catch block
        }
        setTags(customTags || []);
      } catch (error: any) {
        console.error('Error fetching tags (Network or other):', error); // More general error
        setTags([]);
      }
    };

    fetchTags();
  }, [note.tags]);

  const handlePrint = (options: PrintOptions) => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // Build the print content based on selected options
    let printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print - ${note.title}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              line-height: 1.6;
              color: #333;
              max-width: 800px;
              margin: 0 auto;
              padding: 40px 20px;
            }
            h1 {
              color: #1a1a1a;
              border-bottom: 2px solid #e5e5e5;
              padding-bottom: 10px;
              margin-bottom: 30px;
            }
            .meta {
              color: #666;
              font-size: 14px;
              margin-bottom: 30px;
              padding: 15px;
              background: #f8f9fa;
              border-radius: 8px;
            }
            .tags {
              margin: 20px 0;
            }
            .tag {
              display: inline-block;
              padding: 4px 12px;
              margin: 2px 4px 2px 0;
              background: #e9ecef;
              border-radius: 16px;
              font-size: 12px;
              color: #495057;
            }
            .content {
              margin: 30px 0;
            }
            pre {
              background: #000;
              color: #e5e5e5;
              padding: 20px;
              border-radius: 8px;
              overflow-x: auto;
              font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
              font-size: 13px;
              line-height: 1.4;
            }
            code {
              background: #000;
              color: #e5e5e5;
              padding: 2px 6px;
              border-radius: 4px;
              font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
              font-size: 13px;
            }
            pre code {
              background: none;
              padding: 0;
            }
            blockquote {
              border-left: 4px solid #ddd;
              margin: 20px 0;
              padding-left: 20px;
              color: #666;
              font-style: italic;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 12px;
              text-align: left;
            }
            th {
              background: #f8f9fa;
              font-weight: 600;
            }
            .footer {
              margin-top: 60px;
              padding-top: 20px;
              border-top: 1px solid #e5e5e5;
              text-align: center;
              color: #666;
              font-size: 14px;
            }
            .logo {
              display: inline-flex;
              align-items: center;
              gap: 8px;
              font-weight: 600;
              color: #4f46e5;
            }
            @media print {
              body { margin: 0; padding: 20px; }
              .footer { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
    `;

    if (options.includeTitle) {
      printContent += `<h1>${note.title}</h1>`;
    }

    if (user) {
      printContent += `
        <div class="meta">
          <div><strong>Author:</strong> ${user.name}</div>
          <div><strong>Created:</strong> ${formatDate(note.createdAt)}</div>
          ${note.updatedAt !== note.createdAt ? `<div><strong>Updated:</strong> ${formatDate(note.updatedAt)}</div>` : ''}
        </div>
      `;
    }

    if (options.includeTags && tags.length > 0) {
      printContent += `
        <div class="tags">
          <strong>Tags:</strong><br>
          ${tags.map(tag => `<span class="tag">${tag.name}</span>`).join('')}
        </div>
      `;
    }

    if (options.includeContent) {
      // Convert markdown to HTML for printing
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = note.content
        .replace(/```(\w+)?\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>')
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/^# (.*$)/gim, '<h1>$1</h1>')
        .replace(/^## (.*$)/gim, '<h2>$1</h2>')
        .replace(/^### (.*$)/gim, '<h3>$1</h3>')
        .replace(/^\> (.*$)/gim, '<blockquote>$1</blockquote>')
        .replace(/\n/g, '<br>');
      
      printContent += `<div class="content">${tempDiv.innerHTML}</div>`;
    }

    printContent += `
          <div class="footer">
            <div class="logo">
              <span style="font-family: monospace; font-size: 16px;">&lt;/&gt;</span>
              CodeNest
            </div>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load then print
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="fixed inset-4 bg-background rounded-lg shadow-xl overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b p-4">
          <div>
            <div className="flex items-center gap-4 mb-2">
              <h2 className="text-2xl font-bold">{note.title}</h2>
              {note.is_public && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                  <Globe className="w-3 h-3" />
                  Public
                </span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              {/* Changed to flex column */}
              <div className="flex items-center gap-4">
                {/* Container for avatar and date */}
                {user && (
                  <div className="flex items-center gap-2">
                    <AvatarTooltip
                      name={user.name}
                      url={user.avatar_url}
                      size="sm"
                    />
                    <time className="text-sm text-muted-foreground">
                      {formatDate(note.createdAt)}
                    </time>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {/* Tags container */}
                {tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="px-2 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: `${tag.color}20`,
                      color: tag.color,
                    }}
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowPrintModal(true)}
              className="h-9 w-9 p-0"
            >
              <Printer className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onShare}
              className="h-9 w-9 p-0"
            >
              <Share2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-9 w-9 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
          <div className="prose prose-base max-w-none dark:prose-invert">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                code({ node, inline, className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || '');
                  if (!inline && match) {
                    return (
                      <BasicCodeBlock className={className}>
                        {String(children).replace(/\n$/, '')}
                      </BasicCodeBlock>
                    );
                  }
                  return (
                    <code
                      className="px-1.5 py-0.5 rounded-md bg-black text-gray-300 font-mono text-sm"
                      {...props}
                    >
                      {children}
                    </code>
                  );
                },
                table: CustomTable,
                thead: CustomThead,
                th: CustomTh,
                tr: CustomTr,
                td: CustomTd,
                input({ type, checked }) {
                  if (type === 'checkbox') {
                    return (
                      <CustomCheckbox
                        state={checked ? 'checked' : 'unchecked'}
                      />
                    );
                  }
                  return null;
                },
                sup({ children }) {
                  if (String(children).startsWith('^')) {
                    return (
                      <sup className="text-primary">
                        {children}
                        <FootnoteBackButton />
                      </sup>
                    );
                  }
                  return <sup>{children}</sup>;
                },
              }}
            >
              {note.content}
            </ReactMarkdown>
          </div>
        </div>
      </motion.div>

      <PrintModal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        onPrint={handlePrint}
        note={{
          title: note.title,
          content: note.content,
          tags: tags.map(tag => tag.name)
        }}
      />
    </motion.div>
  );
};

export default NoteViewer;
